import { useRouter } from 'next/router';
import useSWR from 'swr/immutable';
import { removeHost } from '@packages/shared';
import { TrackingContext } from '@packages/tracking';
import { useContext } from 'react';
import { getFullLocale } from '@packages/config';
import { useConfig, useCookies } from '@packages/utilities';
import { API_URL } from '../api/rest/endpoints';
import { searchFetcher } from '../api/rest/fetchers';
import type { SearchApiResponse, SearchApiRequestOptions } from '../api/rest/types';
import { decodeSearchQuery, decodeSharedQueryParamsForRestApi } from '../queryEncoding';
import { cleanupCategoryPath } from '../utils/categoryPaths';
import { getNitroIdFromCookies } from '../utils/getNitroIdFromCookies';
import { PERSONALIZATION_COOKIE_NAME } from './usePersonalizationCookie';
import { useChannel } from './useChannel';

/** apply compatibility fixes for differences between legacy API model and desired model */
const transformLegacyApi = (data: SearchApiResponse): SearchApiResponse => ({
  ...data,
  searchresult: {
    ...data?.searchresult,
    result: {
      ...data?.searchresult?.result,
      filters:
        data?.searchresult?.result?.filters?.map((filter) =>
          'slider' in filter
            ? {
                ...filter,
                // API is not yet migrated to send correct type, hence this workaround
                type: 'slider',
              }
            : filter,
        ) ?? [],
    },
  },
});

export const useSearchResult = (
  /**
   * For which page (with current paging mode and page size) to fetch the data, 1-based
   */
  pageNumber?: number,
) => {
  const {
    clientId,
    i18n: { defaultLocale },
  } = useConfig();
  const channel = useChannel();

  const { asPath, pathname, query, locale } = useRouter();
  const { isBot } = useContext(TrackingContext) ?? { isBot: false };
  const getLocalizedCategoryPath = (
    language: string | undefined,
    pathWithoutLanguagePrefix: string,
  ) =>
    `${
      language && !defaultLocale.startsWith(language) ? `/${language}` : ''
    }${pathWithoutLanguagePrefix}`;

  const searchquery = decodeSearchQuery(query);

  const { getCookies } = useCookies();
  const cookies = getCookies();

  const options: SearchApiRequestOptions = {
    locale: getFullLocale(locale),
    clientId,
    ...decodeSharedQueryParamsForRestApi(query),
    ...(pathname.startsWith('/s/') && searchquery
      ? { query: searchquery, category: typeof query.c === 'string' ? query.c : undefined }
      : { uri: cleanupCategoryPath(getLocalizedCategoryPath(locale, asPath)) }),
    isBot,
    ...(pageNumber && { pageNoDisplay: pageNumber }),
    personalization: cookies[PERSONALIZATION_COOKIE_NAME],
    nitroId: getNitroIdFromCookies(cookies),
    lmPromo: typeof query.lmPromo === 'string' ? query.lmPromo : undefined,
    channel,
  };

  const cacheKey =
    pageNumber && pageNumber > 1 ? [removeHost(API_URL), options] : 'search-api-result';

  const { data, error, isLoading } = useSWR(cacheKey, () =>
    searchFetcher([removeHost(API_URL), options]),
  );

  // in suspense mode, data should always be defined
  return {
    response: data ? transformLegacyApi(data as SearchApiResponse) : undefined,
    isLoading,
    error,
    options,
  };
};
